import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components
import {
  Box,
  Button,
  // Checkbox,
  PageHeader,
  TableSkeleton,
  generateTableFromSchema,
  useTable,
  Popover,
  Picto,
  Pagination,
  // @ts-ignore
} from '@zolteam/zol-admin-ui';
// import { useMutation, useQueryClient } from '@tanstack/react-query';

// Services
import { useMutation } from '@tanstack/react-query';
import { useGetSpecialCollectionsList } from
  '../../services/special-collections/specialCollectionsQueries';
import { deleteSpecialCollection } from
  '../../services/special-collections/specialCollectionsService';

/**
 * SpecialCollectionList component.
 * @returns {JSX.Element}
 */
function SpecialCollectionList() {
  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  // const queryClient = useQueryClient();

  // -- search-params
  const [searchParam, setSearchParams] = useSearchParams();
  const pageNumber = searchParam.get('pageNumber') || 1;
  const pageSize = searchParam.get('pageSize') || 10;
  const sort = searchParam.get('sort') || null;
  const order = searchParam.get('order') || null;

  const {
    handlePaginationChange,
    handleColumnSort,
    countSelectedRows,
    // isRowSelected,
    // selectedRows,
    dispatch: useTableDispatch,
  } = useTable({
    setParams: (/** @type {object} */ params) => setSearchParams(
      (searchParams) => {
        Object.entries(params).forEach(([key, value]) => {
          searchParams.set(key, value);
        });
        return searchParams;
      },
    ),
    order,
    sort,
  });

  // Services
  const {
    data: dataSpecialCollectionsList,
    isLoading: isLoadingDataSpecialCollectionList,
    refetch,
  } = useGetSpecialCollectionsList(
    { pageNumber: Number(pageNumber), pageSize: Number(pageSize) },
    { keepPreviousData: true },
  );

  // Mutations
  const { mutate: mutateDeleteSpecialCollection } = useMutation({
    mutationFn: (/** @type {{id:string}} */ mutationData) => deleteSpecialCollection(
      mutationData.id,
    ),
    onSuccess: () => refetch(),
  });
  // States
  /**
   * Pagination count label.
   * @param {number} currentPageMin
   * @param {number} currentPageMax
   * @param {number} count
   * @returns {string}
   */
  const paginationCountLabel = (
    currentPageMin,
    currentPageMax,
    count,
  ) => t('cortizol.pagination.resultByPage', {
    currentPageMin,
    currentPageMax,
    count,
  });

  // Handlers
  /**
   * Handle the click on the new content button.
   * @returns {void}
   */
  const handleNewContentClick = () => navigate('/special-collections/new');

  /**
   * Handle the click on the delete button.
   * @param {string} id
   * @param {string} name
   * @returns {void}
   */
  const handleDeleteSpecialCollection = (id, name) => {
    // eslint-disable-next-line no-alert
    if (window.confirm(t('specialCollectionsList.confirmDelete', { name }))) {
      mutateDeleteSpecialCollection({ id });
    }
  };

  /**
   * Handle the click on a row.
   * @param {SpecialCollection} row
   * @returns {void}
   */
  const handleRowNavigate = (row) => navigate(`/special-collections/${row?.id}`);

  /**
   * Format date.
   * @param {Date} date
   * @param {string} time
   * @returns {string}
   */
  const formatDate = (date, time) => {
    const newDate = new Date(`${date}T${time}`);

    // TODO api should return iso date
    newDate.setHours(newDate.getHours() + 1);
    return newDate.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
  };

  return (
    <Box direction="column" gap="s" className="padding-l">
      <PageHeader
        title={(
          <>
            <h1 className="h1-default" style={{ fontWeight: 'bold' }}>
              {t('specialCollectionsList.title')}
            </h1>
            {dataSpecialCollectionsList?.specialCollectionPoints.length ? (
              <span className="font-color-neutral-500">
                {t('specialCollectionsList.nbRecords', {
                  count:
                    dataSpecialCollectionsList.specialCollectionPoints.length,
                })}
              </span>
            ) : null}
          </>
        )}
        actions={(
          <Button icon="add" onClick={handleNewContentClick}>
            {t('specialCollectionsList.createNewSpecialCollection')}
          </Button>
        )}
      />

      {countSelectedRows ? (
        <Box align="center" justify="flex-end" fullWidth>
          {t('specialCollectionsList.nbRecordsSelected', {
            count: countSelectedRows,
          })}
          <Button
            color="noBackground"
            size="small"
            onClick={() => useTableDispatch({ type: 'RESET_SELECTED_ROWS' })}
          >
            {t('specialCollectionsList.reset')}
          </Button>
        </Box>
      ) : null}

      {isLoadingDataSpecialCollectionList ? (
        <TableSkeleton />
      ) : (
        generateTableFromSchema({
          JSONSchema: {},
          data: dataSpecialCollectionsList.specialCollectionPoints,
          sort: {
            order,
            active: sort,
            handleColumnSort,
          },
          navigate: {
            handleRowNavigate,
          },
          labelNoResult: t('specialCollectionsList.noResult'),
          customColumns: [
            {
              key: 'name',
              position: 'start',
              sortable: false,
            },
            {
              key: 'address',
              description: t('specialCollectionsList.garageStreetName'),
              sortable: false,
              position: 'start',
              renderHead: () => t('specialCollectionsList.garageStreetName'),
              renderCell: (/** @type {SpecialCollection} */ row) => (
                <button
                  type="button"
                  onClick={() => handleRowNavigate(row)}
                  className="cell"
                >
                  {row?.address?.streetName
                    ? // eslint-disable-next-line max-len
                    `${row?.address?.streetNumber} ${row?.address?.streetName}, ${row?.address?.postalCode} ${row?.address?.city}`
                    : '-'}
                </button>
              ),
            },
            {
              key: 'startDate',
              position: 'start',
              renderHead: () => t('specialCollectionsList.startDate'),
              renderCell: (/** @type {SpecialCollection} */ row) => (
                <button
                  type="button"
                  onClick={() => handleRowNavigate(row)}
                  className="cell"
                >
                  {row.startDate
                    ? formatDate(row.startDate, row.startTime)
                    : '-'}
                </button>
              ),
            },
            {
              key: 'endDate',
              position: 'start',
              renderHead: () => t('specialCollectionsList.endDate'),
              renderCell: (/** @type {SpecialCollection} */ row) => (
                <button
                  type="button"
                  onClick={() => handleRowNavigate(row)}
                  className="cell"
                >
                  {row.endDate ? formatDate(row.endDate, row.endTime) : '-'}
                </button>
              ),
            },
            {
              key: 'publishedDate',
              position: 'start',
              renderHead: () => t('specialCollectionsList.publishedDate'),
              renderCell: (/** @type {SpecialCollection} */ row) => (
                <button
                  type="button"
                  onClick={() => handleRowNavigate(row)}
                  className="cell"
                >
                  {row.publishedDate
                    ? `${new Date(row.publishedDate).toLocaleDateString()}`
                    : '-'}
                </button>
              ),
            },
            {
              key: 'actions',
              description: '-',
              type: 'custom',
              sortable: false,
              position: 'end',
              renderHead: () => '',
              renderCell: (/** @type {SpecialCollection} */ row) => (
                <Box justify="center">
                  <Popover
                    minWidth
                    animation="fade"
                    placement="left"
                    offset={[0, 0]}
                    content={(
                      <Box direction="column">
                        <Button
                          size="small"
                          color="noBackground"
                          icon="danger"
                          onClick={() => handleDeleteSpecialCollection(row?.id, row?.name)}
                        >
                          {t('specialCollectionsList.delete')}
                        </Button>
                      </Box>
                    )}
                  >
                    <Picto
                      className="font-color-primary-500"
                      icon="more"
                      style={{ height: '22px' }}
                    />
                  </Popover>
                </Box>
              ),
            },
          ],
        })
      )}

      {dataSpecialCollectionsList?.specialCollectionPoints.length
      && dataSpecialCollectionsList?.pagination.recordsCount ? (
        <Pagination
          recordsCount={dataSpecialCollectionsList.pagination.recordsCount}
          pageNumber={+pageNumber}
          pageSize={+pageSize}
          onPageNumberChange={handlePaginationChange}
          countLabelCallback={paginationCountLabel}
          perPageResultLabel={t('cortizol.pagination.resultPerPage')}
        />
        ) : null}
    </Box>
  );
}

export default SpecialCollectionList;
